import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { type ChangeEvent, type DragEvent, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { FileUploadArea } from "~/features/QuoteSplit/components/FileUploadArea";
import { DateFormatType, formatDate, getPattern } from "~/common/utils/DateFormatter";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Input,
  Stack,
  styled,
  TextField,
  Typography,
} from "~/common/mui";
import type { BulkUploadBeforePreviewState } from "../workflow/bulkUploadReducer";
import type { BulkUploadTemplateType } from "../types/contracts";

interface BulkUploadBeforePreviewProps {
  bulkMaxRows?: string;
  disabled: boolean;
  onDownloadTemplate: (type: BulkUploadTemplateType) => void;
  onFileSelected: (file: File) => void;
  onTextChange: (text: string) => void;
  state: BulkUploadBeforePreviewState;
  text: string;
}

export function BulkUploadBeforePreview({
  bulkMaxRows,
  disabled,
  onDownloadTemplate,
  onFileSelected,
  onTextChange,
  state,
  text,
}: BulkUploadBeforePreviewProps) {
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);
  const fileFormRef = useRef<HTMLFormElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isFileDragging, setIsFileDragging] = useState(false);

  const handleFileDragEnter = (event: DragEvent<HTMLElement>) => {
    if (!disabled && event.dataTransfer.types.includes("Files")) {
      setIsFileDragging(true);
    }
  };

  const handleFileDragLeave = (event: DragEvent<HTMLElement>) => {
    if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) {
      return;
    }
    setIsDragging(false);
    setIsFileDragging(false);
  };

  const resetFileDrag = () => {
    setIsDragging(false);
    setIsFileDragging(false);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onFileSelected(file);
    }
    fileFormRef.current?.reset();
  };

  const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!disabled && event.dataTransfer.types.includes("Files")) {
      setIsDragging(true);
    }
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!disabled && event.dataTransfer.types.includes("Files")) {
      event.dataTransfer.dropEffect = "copy";
      setIsDragging(true);
    }
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) {
      return;
    }
    setIsDragging(false);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    setIsFileDragging(false);
    const file = event.dataTransfer.files[0];
    if (!disabled && file) {
      onFileSelected(file);
    }
  };

  const failureLabel =
    state.failure?.error?.reason ?? (state.failure?.kind === "transport" ? "bulkUpload.error.upload" : undefined);

  return (
    <Stack
      data-testid="bulk-upload-before-preview"
      gap={3}
      onDragEnterCapture={handleFileDragEnter}
      onDragLeaveCapture={handleFileDragLeave}
      onDropCapture={resetFileDrag}
    >
      {state.notice === "contextChanged" && (
        <Alert severity="warning">
          {t("bulkUpload.contextChanged", {
            defaultValue: "Your cart or account context changed. Please start Bulk Upload again.",
          })}
        </Alert>
      )}
      {failureLabel && (
        <Alert severity="error">
          {t(failureLabel, {
            bulkMaxRows,
            count: state.failure?.error?.params.length ?? 0,
            param: state.failure?.error?.params.join(", ") ?? "",
          })}
        </Alert>
      )}
      {state.failure?.rowErrors?.map((error) => (
        <Alert
          key={`${error.rowNumber}:${error.field}:${error.code}`}
          data-testid="bulk-upload-structural-row-error"
          severity="error"
        >
          {t(error.code)} — #{error.rowNumber} · {error.field}
        </Alert>
      ))}

      <UploadColumns data-testid="bulk-upload-entry-columns">
        <Stack gap={1}>
          <FileDropArea
            data-file-dragging={isFileDragging || undefined}
            data-dragging={isDragging || undefined}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <form ref={fileFormRef} hidden>
              <Input
                inputRef={fileRef}
                inputProps={{ "data-testid": "bulk-upload-input-file", accept: ".csv,.txt,.xlsx" }}
                disabled={disabled}
                type="file"
                onChange={handleFileChange}
              />
            </form>
            <FileUploadArea
              bulkEntry
              disabled={disabled}
              fileFormatText={t("bulkUpload.uploadDialog.fileFormat")}
              isDragging={isFileDragging}
              selectFile={() => fileRef.current?.click()}
            />
          </FileDropArea>
          <Box>
            <Button startIcon={<FileDownloadOutlinedIcon />} variant="text" onClick={() => onDownloadTemplate("xlsx")}>
              {t("bulkUpload.download.template.text")}
            </Button>
          </Box>
        </Stack>
        <TextEntryField
          multiline
          rows={6}
          fullWidth
          disabled={disabled}
          placeholder={t("bulkUpload.enter.text")}
          value={text}
          helperText={
            <Trans
              i18nKey="bulkUpload.text.helperText"
              values={{ dateFormat: formatDate(new Date(), DateFormatType.DATE) }}
              components={[<Typography component="i" variant="bodySmall" sx={{ fontWeight: "bold" }} />]}
            />
          }
          inputProps={{ "aria-label": t("bulkUpload.text.label"), "data-testid": "bulk-upload-input-text" }}
          onChange={(event) => onTextChange(event.target.value)}
        />
      </UploadColumns>

      <div data-testid="bulk-upload-help-section">
        <HelpAccordion disableGutters>
          <AccordionSummary expandIcon={<KeyboardArrowDownIcon />}>
            <Typography variant="bodyMedium" sx={{ fontWeight: "bold" }}>
              {t("bulkUpload.help.upload")}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack gap={2}>
              <Typography>{t("bulkUpload.help.item1.paragraph1")}</Typography>
              <Typography>{t("bulkUpload.help.item1.paragraph2", { bulkMaxRows })}</Typography>
              <Button variant="text" onClick={() => onDownloadTemplate("xlsx")} sx={{ alignSelf: "flex-start" }}>
                {t("bulkUpload.help.item1.downloadButton1")}
              </Button>
              <Typography>
                {t("bulkUpload.help.item1.paragraph3", { dateFormat: getPattern(DateFormatType.DATE) })}
              </Typography>
            </Stack>
          </AccordionDetails>
        </HelpAccordion>
        <HelpAccordion disableGutters>
          <AccordionSummary expandIcon={<KeyboardArrowDownIcon />}>
            <Typography variant="bodyMedium" sx={{ fontWeight: "bold" }}>
              {t("bulkUpload.help.text")}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack gap={2}>
              <Typography>
                {t("bulkUpload.help.item2.paragraph1", {
                  bulkMaxRows,
                  dateFormat: getPattern(DateFormatType.DATE),
                })}
              </Typography>
              <Typography>{t("bulkUpload.help.item2.paragraph2")}</Typography>
              <Button variant="text" onClick={() => onDownloadTemplate("csv")} sx={{ alignSelf: "flex-start" }}>
                {t("bulkUpload.help.item1.downloadButton2")}
              </Button>
            </Stack>
          </AccordionDetails>
        </HelpAccordion>
      </div>
    </Stack>
  );
}

export function BulkUploadBeforePreviewActions({
  disabled,
  onCancel,
  onTextSubmit,
  text,
}: {
  disabled: boolean;
  onCancel: () => void;
  onTextSubmit: () => void;
  text: string;
}) {
  const { t } = useTranslation();
  return (
    <>
      <Button data-testid="bulk-upload-btn-cancel" variant="text" onClick={onCancel}>
        {t("bulkUpload.buttons.cancel")}
      </Button>
      <Button
        data-testid="bulk-upload-text-submit"
        disabled={disabled || text.trim().length === 0}
        onClick={onTextSubmit}
      >
        {t("bulkUpload.text.button")}
      </Button>
    </>
  );
}

const UploadColumns = styled("div")(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(3),
  gridTemplateColumns: "1fr",
  [theme.breakpoints.up("tablet")]: {
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  },
}));

const FileDropArea = styled("div")(({ theme }) => ({
  width: "100%",
  "& .MuiPaper-root": {
    backgroundColor: theme.palette.surfaceContainer.high,
    border: `1px dashed ${theme.palette.text.secondary}`,
    borderRadius: "8px",
    height: "172px",
    transition: "background-color 0.3s ease, border-color 0.3s ease",
  },
  '&[data-file-dragging="true"] .MuiPaper-root': {
    backgroundColor: theme.palette.primary.container,
    borderColor: theme.palette.primary.main,
    borderStyle: "dashed",
    borderWidth: "2px",
  },
  '&[data-dragging="true"] .MuiPaper-root': {
    backgroundColor: theme.palette.primary.container,
    borderColor: theme.palette.primary.main,
    borderStyle: "solid",
    borderWidth: "2px",
  },
}));

const TextEntryField = styled(TextField)(({ theme }) => ({
  "& .MuiInputBase-root": {
    alignItems: "flex-start",
    height: "172px",
    overflow: "hidden",
  },
  "& .MuiInputBase-inputMultiline": {
    maxHeight: "100%",
    overflowY: "auto",
  },
  "& .MuiFormHelperText-root": {
    margin: theme.spacing(0.5, 2, 0),
  },
}));

const HelpAccordion = styled(Accordion)(({ theme }) => ({
  background: "transparent",
  borderBottom: 0,
  borderRadius: 0,
  borderTop: `1px solid ${theme.palette.divider}`,
  boxShadow: "none",
  margin: "0 !important",
  "&:last-of-type": {
    borderBottom: `1px solid ${theme.palette.divider}`,
    "& .MuiAccordionSummary-root": {
      minHeight: theme.spacing(5.75),
    },
  },
  "&::before": {
    display: "none",
  },
  "&::after": {
    display: "none",
  },
  "& .MuiAccordionSummary-root": {
    minHeight: theme.spacing(5.875),
    padding: theme.spacing(0, 1.5),
  },
  "& .MuiAccordionSummary-content": {
    margin: 0,
  },
}));
